import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';

import type {
  ConditionOption,
  ConditionSection,
  ConditionSelection,
  ConditionSelectionType,
  ConditionValue,
} from './conditions.types';
import {
  flattenOptions,
  matchesValue,
  optionKey,
  toConditionValue,
} from './util/conditionSelection.util';
import { filterOptionTree } from './util/filterOptionTree';
import { getEffectiveKeys, toMinimalCover } from './util/optionCoverage';
import { updateConditionSelection } from './util/updateConditionSelection';

export interface ConditionSelectionContextValue {
  /** The section whose modal is open, `null` when none is */
  activeSection: ConditionSection | null;

  /** What the open section holds in the draft, whatever its shape */
  activeValue: ConditionValue | undefined;

  /** Discards the draft and closes the open section */
  closeSection(): void;

  /** The committed selection with the in-progress edit applied over it */
  draftSelection: ConditionSelection;

  /** Whether the open section options have logos */
  hasLogos: boolean;

  /** Whether the draft has diverged from what is committed */
  isDirty: boolean;

  /** Whether an option is selected in the open section's draft */
  isSelected(option: ConditionOption): boolean;

  /** Whether the open section renders sub-items as collapsible nested lists */
  nested: boolean;

  /** Seeds a draft from whatever the section already holds and opens it */
  openSection(key: string): void;

  /** The open section's options, narrowed by the search query */
  options: ConditionOption[];

  /** Commits the draft through `onChange`, then closes the section */
  save(): void;

  searchQuery: string;

  /** Selects every option the search leaves visible */
  selectAll(): void;

  /** The committed selection, unaffected by an in-progress edit */
  selection: ConditionSelection;

  selectionType: ConditionSelectionType;

  selectNone(): void;

  setSearchQuery(query: string): void;

  setSelectionType(selectionType: ConditionSelectionType): void;

  /** Flips one option, carrying everything nested under it to the same state */
  toggleOption(option: ConditionOption): void;

  /**
   * Replaces the open section's draft wholesale, for the sections that edit a
   * value rather than a list of options — `toggleOption` covers those.
   */
  updateValue(value: ConditionValue): void;
}

const ConditionSelectionContext =
  createContext<ConditionSelectionContextValue | null>(null);

ConditionSelectionContext.displayName = 'ConditionSelection';

export interface ConditionSelectionProviderProps {
  children?: ReactNode;

  /** The sections available to edit */
  conditions: ConditionSection[];

  onChange(
    selectionType: ConditionSelectionType,
    key: string,
    value: ConditionValue
  ): void;

  /** The active selections for the group currently being edited. */
  selection: ConditionSelection;
}

const emptyValueFor = (section: ConditionSection): ConditionValue =>
  section.type === 'multiselect' ? [] : {};

/** Clearing a section leaves its key behind holding `[]` or `{}`, both truthy */
const hasSelection = (value: ConditionValue | undefined): boolean =>
  Array.isArray(value) ? value.length > 0 : Object.keys(value ?? {}).length > 0;

/**
 * Houses a group's condition selections, committed and in-progress, so the
 * section rows and the open section's modal read one editing session.
 */
export const ConditionSelectionProvider = ({
  children,
  conditions,
  onChange,
  selection,
}: ConditionSelectionProviderProps) => {
  const [openSectionKey, setOpenSectionKey] = useState<string | null>(null);
  const [chosenSelectionType, setChosenSelectionType] =
    useState<ConditionSelectionType>('includes');
  const [searchQuery, setSearchQuery] = useState('');
  const [draftValue, setDraftValue] = useState<ConditionValue | undefined>(
    undefined
  );
  const [isDirty, setIsDirty] = useState(false);

  const activeSection = useMemo(
    () => conditions.find((section) => section.key === openSectionKey) ?? null,
    [conditions, openSectionKey]
  );

  /**
   * Derived rather than stored, so a locked section can't be switched away from
   * its type — even if it gained the lock after the modal opened.
   */
  const selectionType = activeSection?.selectionType ?? chosenSelectionType;

  const draftSelection = useMemo(
    () =>
      activeSection
        ? updateConditionSelection(
            selection,
            selectionType,
            activeSection.key,
            draftValue
          )
        : selection,
    [activeSection, draftValue, selection, selectionType]
  );

  const activeValue = activeSection
    ? draftSelection[selectionType][activeSection.key]
    : undefined;

  // matched on value, so a parent the search filter copied still resolves
  const isStored = useCallback(
    (option: ConditionOption) => matchesValue(option.value, activeValue),
    [activeValue]
  );

  // read off the section rather than the filtered options, so what a search
  // hides still counts towards what a stored parent covers
  const effectiveKeys = useMemo(
    () =>
      activeSection?.type === 'multiselect'
        ? getEffectiveKeys(activeSection.items, isStored)
        : new Set<string>(),
    [activeSection, isStored]
  );

  /**
   * A stored parent stands in for its whole subtree, so a row reads as selected
   * when an ancestor was stored — the draft itself only carries the parent.
   */
  const isSelected = useCallback(
    (option: ConditionOption) =>
      isStored(option) || effectiveKeys.has(optionKey(option)),
    [effectiveKeys, isStored]
  );

  // only a multiselect searches its options; the other sections edit values.
  // A section fetching its own options has already applied the query, so
  // filtering again could only narrow what the server meant to match.
  const options = useMemo(() => {
    if (activeSection?.type !== 'multiselect') {
      return [];
    }

    return activeSection.onSearch
      ? activeSection.items
      : filterOptionTree(activeSection.items, searchQuery);
  }, [activeSection, searchQuery]);

  const changeSearchQuery = useCallback(
    (query: string) => {
      setSearchQuery(query);

      if (activeSection?.type === 'multiselect') {
        activeSection.onSearch?.(query);
      }
    },
    [activeSection]
  );

  const nested =
    activeSection?.type === 'multiselect' && !!activeSection.nested;

  // read off the section, not the filtered options, to keep all items consistent
  const hasLogos =
    activeSection?.type === 'multiselect' &&
    flattenOptions(activeSection.items).some((item) => !!item.logo);

  const updateDraft = useCallback((value: ConditionValue) => {
    setDraftValue(value);
    setIsDirty(true);
  }, []);

  const openSection = useCallback(
    (key: string) => {
      const section = conditions.find((item) => item.key === key);
      // A locked section opens as its own type, even with nothing selected. One holding
      // both opens as `includes` (saving as `excludes` would drop the `includes` and widen it).
      const isExcludedOnly =
        hasSelection(selection['excludes'][key]) &&
        !hasSelection(selection['includes'][key]);
      const type =
        section?.selectionType ?? (isExcludedOnly ? 'excludes' : 'includes');

      setOpenSectionKey(key);
      setDraftValue(selection[type][key]);
      setChosenSelectionType(type);
      setSearchQuery('');
      // a section fetching its own options is holding the last query it was
      // given, so clearing the field has to reach it too
      if (section?.type === 'multiselect') {
        section.onSearch?.('');
      }
      setIsDirty(false);
    },
    [conditions, selection]
  );

  const closeSection = useCallback(() => {
    setOpenSectionKey(null);
    setDraftValue(undefined);
    setIsDirty(false);
  }, []);

  const save = useCallback(() => {
    if (activeSection) {
      const other = selectionType === 'includes' ? 'excludes' : 'includes';

      // A section is included or excluded, never both, so saving one side
      // clears whatever the other side held
      if (hasSelection(selection[other][activeSection.key])) {
        onChange(other, activeSection.key, emptyValueFor(activeSection));
      }

      onChange(
        selectionType,
        activeSection.key,
        draftValue ?? emptyValueFor(activeSection)
      );
    }

    closeSection();
  }, [
    activeSection,
    closeSection,
    draftValue,
    onChange,
    selection,
    selectionType,
  ]);

  /**
   * A section is either included or excluded, so switching type picks up
   * whatever that type already holds rather than carrying the draft across.
   */
  const changeSelectionType = useCallback(
    (type: ConditionSelectionType) => {
      setChosenSelectionType(type);
      setDraftValue(
        activeSection ? selection[type][activeSection.key] : undefined
      );
      setIsDirty(true);
    },
    [activeSection, selection]
  );

  const selectAll = useCallback(() => {
    if (!activeSection) {
      return;
    }

    // every row selected at any value, since the bounds are per row
    if (activeSection.type === 'multiselect-range') {
      updateDraft(
        Object.fromEntries(
          activeSection.items.map((item) => [optionKey(item), {}])
        )
      );

      return;
    }

    // a range section has bounds rather than options, so there is nothing to
    // select all of
    if (activeSection.type !== 'multiselect') {
      return;
    }

    updateDraft(
      toConditionValue(
        toMinimalCover(
          activeSection.items,
          new Set(flattenOptions(options).map(optionKey))
        )
      )
    );
  }, [activeSection, options, updateDraft]);

  const selectNone = useCallback(() => {
    if (activeSection) {
      updateDraft(emptyValueFor(activeSection));
    }
  }, [activeSection, updateDraft]);

  const toggleOption = useCallback(
    (option: ConditionOption) => {
      if (activeSection?.type !== 'multiselect') {
        return;
      }

      // the row and everything nested under it, which follows whatever it
      // becomes. Taken off the option as rendered, so a search narrowing the
      // sub-items leaves the ones it hides alone.
      const subtree = activeSection.nested
        ? flattenOptions([option])
        : [option];
      const subtreeKeys = new Set(subtree.map(optionKey));

      // a partly selected subtree clears rather than filling in, matching the
      // unchecked box an indeterminate parent renders
      const selecting = !subtree.some(isSelected);

      const selected = new Set(
        [...effectiveKeys].filter((key) => !subtreeKeys.has(key))
      );

      if (selecting) {
        subtreeKeys.forEach((key) => selected.add(key));
      }

      // the draft carries the cover rather than every checked row, so a whole
      // category persists as itself
      updateDraft(
        toConditionValue(toMinimalCover(activeSection.items, selected))
      );
    },
    [activeSection, effectiveKeys, isSelected, updateDraft]
  );

  const value = useMemo(
    () => ({
      activeSection,
      activeValue,
      closeSection,
      draftSelection,
      hasLogos,
      isDirty,
      isSelected,
      nested,
      openSection,
      options,
      save,
      searchQuery,
      selectAll,
      selection,
      selectionType,
      selectNone,
      setSearchQuery: changeSearchQuery,
      setSelectionType: changeSelectionType,
      toggleOption,
      updateValue: updateDraft,
    }),
    [
      activeSection,
      activeValue,
      changeSearchQuery,
      changeSelectionType,
      closeSection,
      draftSelection,
      hasLogos,
      isDirty,
      isSelected,
      nested,
      openSection,
      options,
      save,
      searchQuery,
      selectAll,
      selection,
      selectionType,
      selectNone,
      toggleOption,
      updateDraft,
    ]
  );

  return (
    <ConditionSelectionContext.Provider value={value}>
      {children}
    </ConditionSelectionContext.Provider>
  );
};

export const useConditionSelection = () =>
  useContextEnsuringProvider(ConditionSelectionContext);
